import React, { FC, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import AddIcon from 'transfix-icons/build/AddIcon'
import { Card } from 'transfix-ui/components/Card'
import { IconButton } from 'transfix-ui/components/IconButton'
import { fetchChildUsers, clearUndelete } from 'utils/actions/user'
import { IChildUserState } from 'utils/reducers/childUsers'
import uuid from 'uuid/v4'
import { IChildUser, IParentUser } from 'apps/user/types/user'
import { selectUndeletedSuccess, selectUndeletedId } from 'apps/user/utils/selectors/userProfile'
import { DeleteUserAccountModal } from './DeleteUserAccountModal'
import { UserAccountModal } from './UserAccountModal'
import { UserAccountRow } from './UserAccountRow'

interface IStore {
  childUsers: IChildUserState
}

interface IUserAccountsProps {
  parentUser: IParentUser
}

/**
 * TODO: Refactor User Profile
 *
 * 1. Normalize the data, ie: childUser relationships to parentUser
 * 2. Use reselect selectors
 * 3. Multiple useState can be replaced with useReducer
 * Ticket: https://transfix.atlassian.net/browse/SO-1213
 *
 * */

export const UserAccounts: FC<IUserAccountsProps> = ({ parentUser }) => {
  const [showUserAccountModal, setShowUserAccountModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [userToBeEdited, setUserToBeEdited] = useState<IChildUser>()
  const [userToBeDeleted, setUserToBeDeleted] = useState<IChildUser>()

  const { users } = useSelector<IStore, IChildUserState>(({ childUsers }) => ({
    ...childUsers,
  }))

  const dispatch = useDispatch()
  const undeleteSuccess = useSelector(selectUndeletedSuccess)
  const undeleteId = useSelector(selectUndeletedId)

  useEffect(() => {
    if (undeleteSuccess && undeleteId) {
      dispatch(fetchChildUsers(parentUser.id))

      setTimeout(() => dispatch(clearUndelete()), 3000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undeleteSuccess, undeleteId])

  const setEditModalForUser = (userId?: string): void => {
    if (userId) {
      const userInList = users.find(user => String(user.id) === String(userId))
      setUserToBeEdited(userInList)
    }
    setShowUserAccountModal(true)
  }

  const setDeleteModalForUser = (userId: string): void => {
    const userInList = users.find(user => String(user.id) === String(userId))

    setShowDeleteModal(true)
    setUserToBeDeleted(userInList)
  }

  const resetUserAccountModal = () => {
    setShowUserAccountModal(false)
    setUserToBeEdited(undefined)
  }

  const resetDeleteModal = (): void => {
    setShowDeleteModal(false)
    setUserToBeDeleted(undefined)
  }

  const openUserAccountModal = () => {
    setEditModalForUser()
  }

  return (
    <>
      <UserAccountModal
        showModal={showUserAccountModal}
        parentUser={parentUser}
        user={userToBeEdited}
        closeModalHandler={resetUserAccountModal}
      />
      {userToBeDeleted && (
        <DeleteUserAccountModal
          user={userToBeDeleted}
          showModal={showDeleteModal}
          closeModalHandler={resetDeleteModal}
        />
      )}
      <Card
        title='Carrier Accounts'
        callToAction={
          <IconButton
            Icon={AddIcon}
            color='tertiary'
            label='Add Carrier Account'
            onClick={openUserAccountModal}
            data-test='add-carrier-account'
            size='large'
          />
        }>
        <ul className='user-accounts'>
          {users.map(user => (
            <UserAccountRow
              key={uuid()}
              user={user}
              canBeDeleted={users.length !== 1}
              editUser={setEditModalForUser}
              deleteUser={setDeleteModalForUser}
            />
          ))}
        </ul>
      </Card>
    </>
  )
}
